import {
  ActionIcon,
  Box,
  Group,
  Paper,
  TextInput,
  Button,
  Text,
  FileInput,
} from "@mantine/core";
import React, { useState, useEffect, useRef } from "react";
import { X, Plus, PlayerPlay, PlayerStop, Upload, TrashX } from "tabler-icons-react";
import { DEFAULT_API } from "../../../../routes/api.routes";

function AudioRecordingWithTimerForm({ form, index }) {
  const currentEpreuve = form.values.epreuves[index];
  const isdicteeSyllabe = currentEpreuve?.type === 'dicteeSyllabe';

  // Pour dictée syllabe, on utilise un système similaire à WordByWordTranscription
  const [syllabes, setSyllabes] = useState([]);

  // Aperçu audio : joue via le même endpoint TTS que le PWA, donc la voix et
  // la vitesse entendues ici sont celles que l'élève entendra. L'endpoint
  // exige un token Bearer : on passe par fetch() + blob, car <audio src> ne
  // peut pas envoyer d'en-tête Authorization.
  const [playingText, setPlayingText] = useState(null);
  const audioRef = useRef(null);
  const requestRef = useRef(0);

  const getSpeechSpeed = (text) => {
    const trimmed = text.trim();
    if (trimmed.length <= 2) return 0.6;
    if (!/\s/.test(trimmed)) return 0.65;
    return currentEpreuve?.type === 'dictee' ? 0.9 : 0.85;
  };

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src?.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current = null;
    }
    setPlayingText(null);
  };

  const playPreview = async (text) => {
    const spoken = text?.trim();
    if (!spoken) return;
    if (playingText === spoken) {
      stopPreview();
      return;
    }
    stopPreview();
    const requestId = ++requestRef.current;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${DEFAULT_API}tts/?text=${encodeURIComponent(spoken)}&speed=${getSpeechSpeed(spoken)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.ok) throw new Error(`TTS HTTP ${response.status}`);
      const blob = await response.blob();
      if (requestId !== requestRef.current) return;
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      setPlayingText(spoken);
      audio.onended = () => { if (audioRef.current === audio) stopPreview(); };
      audio.onerror = () => { if (audioRef.current === audio) stopPreview(); };
      await audio.play();
    } catch {
      if (requestId === requestRef.current) stopPreview();
    }
  };

  // Joue directement un fichier audio (URL serveur ou blob local sélectionné),
  // sans passer par le TTS : on écoute le fichier tel quel.
  const playFile = (url) => {
    if (!url) return;
    if (playingText === url) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingText(url);
    audio.onended = () => { if (audioRef.current === audio) stopPreview(); };
    audio.onerror = () => { if (audioRef.current === audio) stopPreview(); };
    audio.play().catch(() => stopPreview());
  };

  useEffect(() => () => audioRef.current?.pause(), []);

  // Pour dictée classique
  const consigneString = currentEpreuve?.consigne?.[0] || "";
  const consigneObj = consigneString ? JSON.parse(consigneString) : { temps_lecture: "", mot: "" };
  const correctionValue = currentEpreuve?.correction_dp?.[0] || "";
  const prononciationValue = currentEpreuve?.prononciation_dp?.[0] || "";

  // Initialize syllabes for dicteeSyllabe type
  useEffect(() => {
    if (isdicteeSyllabe) {
      const existingSyllabes = currentEpreuve?.syllabes || [];
      const correctionDp = currentEpreuve?.correction_dp || [];
      const prononciationDp = currentEpreuve?.prononciation_dp || [];

      if (existingSyllabes.length > 0) {
        setSyllabes(existingSyllabes);
      } else if (correctionDp.length > 0) {
        const syllabesFromData = correctionDp.map((syllabe, syllabeIndex) => ({
          id: Date.now() + syllabeIndex,
          value: syllabe,
          prononciation: prononciationDp[syllabeIndex] || "",
        }));
        setSyllabes(syllabesFromData);
        form.setFieldValue(`epreuves.${index}.syllabes`, syllabesFromData);
      }
    }
  }, [currentEpreuve?.correction_dp, currentEpreuve?.syllabes, isdicteeSyllabe]);

  // Debug log
  console.log('AudioRecordingWithTimerForm Debug:', {
    index,
    isdicteeSyllabe,
    currentEpreuve,
    syllabes
  });

  // Fonction pour nettoyer la saisie des mots/phrases (dictée classique)
  const cleanWordInput = (value) => {
    let val = value;
    // Nettoyer la valeur : autoriser seulement les lettres, chiffres, espaces et apostrophes
    // Supprimer tous les caractères spéciaux sauf les espaces et apostrophes
    val = val.replace(/[^a-zA-ZÀ-ÿ0-9\s']/g, '');
    // Limiter les espaces multiples à un seul espace
    val = val.replace(/\s+/g, ' ');
    return val;
  };

  // Fonction pour nettoyer la saisie des syllabes (dictée par syllabe)
  const cleanSyllabeInput = (value) => {
    let val = value;
    // Pour les syllabes : autoriser seulement les lettres et apostrophes (pas de chiffres ni espaces)
    val = val.replace(/[^a-zA-ZÀ-ÿ']/g, '');
    return val;
  };

  // Prononciation lue par la voix IA : on autorise aussi les tirets, utiles
  // pour découper un mot inventé en syllabes (ex. "bi-yé-na-fou").
  const cleanPrononciationInput = (value) =>
    value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').replace(/\s+/g, ' ');

  const handlePrononciationChange = (e) => {
    const cleanedValue = cleanPrononciationInput(e.target.value);
    form.setFieldValue(
      `epreuves.${index}.prononciation_dp`,
      cleanedValue.trim() ? [cleanedValue] : []
    );
  };

  const handleWordChange = (e) => {
    const cleanedValue = cleanWordInput(e.target.value);

    // Update correction_dp
    form.setFieldValue(`epreuves.${index}.correction_dp`, [cleanedValue]);

    // Update consigne with temps_lecture and mot as JSON string
    const newObj = { temps_lecture: consigneObj.temps_lecture || "", mot: cleanedValue };
    form.setFieldValue(`epreuves.${index}.consigne`, [JSON.stringify(newObj)]);
  };

  // Fonctions pour gérer les syllabes (dicteeSyllabe)
  // correction_dp et prononciation_dp sont construits à partir des mêmes
  // syllabes non vides, pour rester alignés case par case.
  const syncSyllabes = (updatedSyllabes) => {
    setSyllabes(updatedSyllabes);
    form.setFieldValue(`epreuves.${index}.syllabes`, updatedSyllabes);
    const filled = updatedSyllabes.filter(syllabe => syllabe.value.trim() !== '');
    form.setFieldValue(`epreuves.${index}.correction_dp`, filled.map(syllabe => syllabe.value));
    form.setFieldValue(
      `epreuves.${index}.prononciation_dp`,
      filled.map(syllabe => syllabe.prononciation || '')
    );
  };

  const addSyllabe = () => {
    syncSyllabes([...syllabes, { id: Date.now(), value: "", prononciation: "" }]);
  };

  const removeSyllabe = (syllabeId) => {
    syncSyllabes(syllabes.filter(syllabe => syllabe.id !== syllabeId));
  };

  const updateSyllabe = (syllabeId, field, value) => {
    const cleanedValue = field === 'value'
      ? cleanSyllabeInput(value)
      : cleanPrononciationInput(value);
    syncSyllabes(syllabes.map(syllabe =>
      syllabe.id === syllabeId ? { ...syllabe, [field]: cleanedValue } : syllabe
    ));
  };

  // --- Audio pré-enregistré (optionnel) ---------------------------------
  // L'admin peut déposer un fichier audio par case (aligné sur correction_dp).
  // Si aucun fichier n'est déposé, le PWA garde son comportement actuel :
  // TTS serveur puis voix du navigateur. Aucune régression.
  const audioDp = currentEpreuve?.audio_dp || [];
  const pendingAudios = currentEpreuve?._pendingAudios || [];
  // Cache position -> blob URL : sans ça, createObjectURL() recréé à chaque
  // render casse la comparaison playingText === url et fuit de la mémoire.
  const blobUrlsRef = useRef({});

  const setPendingAudio = (position, file) => {
    if (blobUrlsRef.current[position]) {
      URL.revokeObjectURL(blobUrlsRef.current[position]);
      delete blobUrlsRef.current[position];
    }
    const others = pendingAudios.filter(a => a.position !== position);
    const next = file ? [{ position, file }] : [];
    form.setFieldValue(`epreuves.${index}._pendingAudios`, [...others, ...next]);
  };

  const audioUrlFor = (position) => {
    const pending = pendingAudios.find(a => a.position === position);
    if (pending?.file) {
      if (!blobUrlsRef.current[position]) {
        blobUrlsRef.current[position] = URL.createObjectURL(pending.file);
      }
      return blobUrlsRef.current[position];
    }
    const url = audioDp?.[position];
    if (!url) return null;
    if (url.startsWith("/")) {
      const base = (DEFAULT_API || "").replace(/api\/v1\/?$/, "").replace(/\/$/, "");
      return base + url;
    }
    return url;
  };

  return (
    <Paper mb={20} p={10} shadow="xs">
      <Group noWrap position="apart">
        <Box style={{ flex: 1 }}>
          <TextInput
            label="Titre de l'exercice"
            placeholder={isdicteeSyllabe ? "Ex: Dictée par syllabe" : "Ex: Dictée audio"}
            {...form.getInputProps(`epreuves.${index}.titre`)}
            mb={10}
          />

          {!isdicteeSyllabe && (
            <TextInput
              label="Temps estimé (en secondes)"
              placeholder="Ex: 5"
              type="number"
              value={consigneObj.temps_lecture}
              onChange={(e) => {
                const newObj = {
                  ...consigneObj,
                  temps_lecture: e.target.value === "" ? "" : e.target.value,
                };
                form.setFieldValue(`epreuves.${index}.consigne`, [JSON.stringify(newObj)]);
              }}
              mb={10}
              description="Estimez le temps nécessaire pour compléter cette transcription"
            />
          )}

          {isdicteeSyllabe ? (
            <>
              <Text size="sm" mb={10} color="dimmed">
                Ajoutez les syllabes à dicter
              </Text>

              <Group spacing="xs" mb={10} wrap>
                {syllabes.map((syllabe, syllabeIndex) => {
                  const audioUrl = audioUrlFor(syllabeIndex);
                  return (
                    <Group key={syllabe.id} spacing="xs" align="center">
                      <Text size="sm" weight={500}>
                        {syllabeIndex + 1}.
                      </Text>
                      <TextInput
                        placeholder="Syllabe"
                        value={syllabe.value}
                        onChange={(e) => updateSyllabe(syllabe.id, 'value', e.target.value)}
                        size="sm"
                        style={{ width: '120px' }}
                      />
                      <TextInput
                        placeholder="Prononciation"
                        value={syllabe.prononciation || ""}
                        onChange={(e) => updateSyllabe(syllabe.id, 'prononciation', e.target.value)}
                        size="sm"
                        style={{ width: '120px' }}
                      />
                      <ActionIcon
                        color="blue"
                        size="sm"
                        onClick={() => playPreview(syllabe.prononciation || syllabe.value)}
                        disabled={!(syllabe.prononciation || syllabe.value)?.trim()}
                        title="Écouter ce que l'élève entendra"
                      >
                        {playingText === (syllabe.prononciation || syllabe.value)?.trim()
                          ? <PlayerStop size="1rem" />
                          : <PlayerPlay size="1rem" />}
                      </ActionIcon>
                      <FileInput
                        placeholder="Audio..."
                        size="sm"
                        style={{ width: '140px' }}
                        accept="audio/*"
                        value={pendingAudios.find(a => a.position === syllabeIndex)?.file || null}
                        onChange={(file) => setPendingAudio(syllabeIndex, file)}
                        icon={<Upload size={14} />}
                        title="Fichier audio pré-enregistré (remplace la voix IA)"
                      />
                      {audioUrl && (
                        <ActionIcon
                          color="green"
                          size="sm"
                          onClick={() => playFile(audioUrl)}
                          title="Écouter le fichier audio"
                        >
                          {playingText === audioUrl ? <PlayerStop size="1rem" /> : <PlayerPlay size="1rem" />}
                        </ActionIcon>
                      )}
                      <ActionIcon
                        color="red"
                        size="sm"
                        onClick={() => removeSyllabe(syllabe.id)}
                      >
                        <X size="1rem" />
                      </ActionIcon>
                    </Group>
                  );
                })}
              </Group>

              <Button
                leftIcon={<Plus size={16} />}
                variant="outline"
                onClick={addSyllabe}
                size="sm"
                mb={10}
              >
                Ajouter une syllabe
              </Button>

              <Text size="xs" color="dimmed">
                Seuls les lettres et apostrophes sont autorisés pour les syllabes (pas de chiffres ni espaces)
              </Text>
              <Text size="xs" color="dimmed">
                Prononciation (optionnel) : à remplir seulement si la voix lit mal la syllabe. Ex : « et » pour « é », « hein » pour « in », « eux » pour « eu ».
              </Text>
              <Text size="xs" color="dimmed">
                Audio (optionnel) : fichier pré-enregistré qui remplace la voix IA pour cette syllabe. Bouton bleu = voix IA, bouton vert = fichier audio.
              </Text>
            </>
          ) : (
            <>
              <TextInput
                label="Mot ou phrase attendue"
                placeholder="Tapez ici le mot ou la phrase attendue pour l'élève"
                value={correctionValue}
                onChange={handleWordChange}
                mb={10}
                description="Seuls les lettres, chiffres, espaces et apostrophes sont autorisés"
                rightSection={
                  <ActionIcon
                    color="blue"
                    onClick={() => playPreview(correctionValue)}
                    disabled={!correctionValue?.trim()}
                    title="Écouter la voix sur le mot écrit"
                  >
                    {playingText === correctionValue?.trim()
                      ? <PlayerStop size="1rem" />
                      : <PlayerPlay size="1rem" />}
                  </ActionIcon>
                }
              />
              <TextInput
                label="Prononciation pour la voix (optionnel)"
                placeholder="Ex : bi-yé-na-fou"
                value={prononciationValue}
                onChange={handlePrononciationChange}
                mb={10}
                description="À remplir seulement si la voix lit mal le mot. Écrivez-le comme il se prononce. L'élève devra toujours écrire le mot attendu ci-dessus."
                rightSection={
                  <ActionIcon
                    color="blue"
                    onClick={() => playPreview(prononciationValue || correctionValue)}
                    disabled={!(prononciationValue || correctionValue)?.trim()}
                    title="Écouter ce que l'élève entendra"
                  >
                    {playingText === (prononciationValue || correctionValue)?.trim()
                      ? <PlayerStop size="1rem" />
                      : <PlayerPlay size="1rem" />}
                  </ActionIcon>
                }
              />
              <Text size="sm" weight={500} mt={10} mb={6}>
                Audio pré-enregistré (optionnel)
              </Text>
              <Text size="xs" color="dimmed" mb={8}>
                Si fourni, ce fichier remplace la voix IA pour cet exercice. Bouton bleu = voix IA, bouton vert = fichier audio.
              </Text>
              <Group spacing="xs" align="center">
                <FileInput
                  placeholder="Aucun fichier"
                  size="sm"
                  style={{ flex: 1 }}
                  accept="audio/*"
                  value={pendingAudios.find(a => a.position === 0)?.file || null}
                  onChange={(file) => setPendingAudio(0, file)}
                  icon={<Upload size={14} />}
                />
                {audioUrlFor(0) && (
                  <ActionIcon
                    color="green"
                    size="sm"
                    onClick={() => playFile(audioUrlFor(0))}
                    title="Écouter le fichier audio"
                  >
                    {playingText === audioUrlFor(0) ? <PlayerStop size="1rem" /> : <PlayerPlay size="1rem" />}
                  </ActionIcon>
                )}
              </Group>
            </>
          )}
        </Box>
        <ActionIcon
          color="red"
          onClick={() => form.removeListItem('epreuves', index)}
        >
          <X size="1rem" />
        </ActionIcon>
      </Group>
    </Paper>
  );
}

export default AudioRecordingWithTimerForm;
